/**
 * La ficha de un torneo: el banner, los patrocinadores, quién está inscrito,
 * el reglamento del club y el cuadro de cada categoría.
 *
 * Mientras está en BORRADOR se puede inscribir gente y acomodar la siembra
 * arrastrando. Al tirarlo pasa a EN JUEGO y el orden queda fijo: a partir de
 * ahí hay partidos jugados contra ese orden y cambiarlo sería reescribir
 * resultados.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  categoryKey,
  SPONSOR_LIMITS,
  type CategoryBracket,
  type CategoryLevel,
  type CommunityCategory,
  type Match,
  type MatchScore,
  type PlayerSearchResult,
  type Tournament,
  type MatchSchedule,
  type TournamentCategory,
  type TournamentEntry,
  type TournamentFormat,
  ENTRY_PAYMENT_METHODS,
  TOURNAMENT_FORMATS,
  TOURNAMENT_LIMITS,
  type EntryPaymentMethod,
  enterableCategories,
  minimumEntriesFor,
  whyNotEnterable,
} from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { TournamentNotes } from '../components/TournamentNotes';
import { AccountsBox } from '../components/AccountsBox';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { Avatar } from '../components/Avatar';
import { Paginador, usePaginado } from '../components/Paginado';
import { PersonCell } from '../components/PersonCell';
import { Bracket } from '../components/Bracket';
import { EntryRoster } from '../components/EntryRoster';
import { Groups, GroupPreview } from '../components/Groups';
import { Select } from '../components/Select';
import { CategoryPicker, formatColones } from '../components/CategoryPicker';
import { ImagePicker } from '../components/ImagePicker';
import { MoneyField } from '../components/MoneyField';
import { NextMatches } from '../components/NextMatches';
import { ScheduleEditor } from '../components/ScheduleEditor';
import { ScoreEditor, sidesOfMatch } from '../components/ScoreEditor';
import { SeedList } from '../components/SeedList';
import { SponsorCarousel } from '../components/SponsorCarousel';
import { useAuth } from '../auth/AuthContext';
import { usePlayerSearch, MIN_SEARCH_LENGTH } from '../hooks/usePlayerSearch';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { formatDate } from './TournamentsPage';
import { FinalsRules } from '../components/FinalsRules';
import { categoryName } from '../lib/categoryName';

export function TournamentPage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id = '' } = useParams();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [brackets, setBrackets] = useState<CategoryBracket[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [ficha, cuadros, partidos] = await Promise.all([
        api.findTournament(id),
        api.tournamentBrackets(id),
        api.tournamentMatches(id),
      ]);
      setTournament(ficha);
      setBrackets(cuadros);
      setMatches(partidos);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Todo lo que cambia el torneo devuelve el torneo entero, así que se guarda
   * y se vuelven a pedir los cuadros. Los cuadros NO se calculan acá: una
   * inscripción cambia tamaños, huecos y siembras, y tener dos lugares que
   * decidan cómo queda el cuadro es tener dos cuadros distintos.
   */
  const applied = useCallback(async (actualizado: Tournament) => {
    setTournament(actualizado);
    const [cuadros, partidos] = await Promise.all([
      api.tournamentBrackets(actualizado.id),
      api.tournamentMatches(actualizado.id),
    ]);
    setBrackets(cuadros);
    setMatches(partidos);
  }, []);

  if (error !== null && tournament === null) {
    return (
      <AppLayout>
        <Alert kind="error">{error}</Alert>
        <p className="links">
          <Link to="/clubes">{t('newCommunity.backToList')}</Link>
        </p>
      </AppLayout>
    );
  }

  if (tournament === null) {
    return (
      <AppLayout>
        <p className="help">{t('common.loading')}</p>
      </AppLayout>
    );
  }

  const banner = assetUrl(tournament.bannerPath);
  /**
   * `editable` es «todavía se puede inscribir gente en este torneo».
   *
   * MIRA QUE NO HAYA TERMINADO, NO QUE SIGA EN BORRADOR. Decía `=== 'DRAFT'`
   * y eso escondía la caja de inscribir entera apenas se tiraba el PRIMER
   * cuadro: el torneo pasa a EN JUEGO ahí, pero las demás categorías siguen
   * abiertas y esperando gente. Es lo que le pasó a Javier con «Primavero
   * Mujeres»: el torneo en juego, la categoría C sin sortear, y ni forma de
   * agregar a nadie.
   *
   * QUIÉN PUEDE ENTRAR EN QUÉ NO SE DECIDE ACÁ. Lo decide
   * `enterableCategories`, que mira el `startedAt` de CADA categoría, y
   * `whyNotEnterable` explica el caso de quien no puede. Esta bandera solo
   * dice si la caja tiene sentido; con el torneo terminado no lo tiene,
   * porque ninguna categoría puede recibir a nadie.
   *
   * Lo que es POR CUADRO —sembrar, sortear, tirar— mira el `startedAt` de su
   * categoría, que es lo único que sabe si ESE cuadro ya está fijo.
   */
  const editable = tournament.canManage && tournament.status !== 'FINISHED';

  return (
    <AppLayout>
      <BackButton to={`/clubes/${tournament.communityId}/torneos`} />

      {banner !== null && <img className="community-banner" src={banner} alt="" />}

      <BasicsSection tournament={tournament} onChanged={applied} />

      {error !== null && <Alert kind="error">{error}</Alert>}

      <SponsorCarousel sponsors={tournament.sponsors} />

      {/*
        LAS CATEGORÍAS LAS VE CUALQUIERA, no solo quien administra.

        Es la segunda pregunta de quien abre un torneo —después de cuándo es—
        y la única forma de contestarla era bajar hasta los cuadros y leer los
        encabezados. Quien administra ve además el botón para cambiarlas.
      */}
      <TournamentCategoriesSection tournament={tournament} onChanged={applied} />

      <EntriesSection tournament={tournament} editable={editable} onChanged={applied} />

      {/* La NÓMINA: quiénes vienen, en qué categoría y quién pagó. Va debajo
          de la caja de agregar porque quien organiza primero agrega y después
          revisa, y para todos los demás la de agregar no existe. */}
      <EntryRoster
        tournament={tournament}
        viewerId={session?.user.id ?? ''}
        viewerSex={session?.user.sex ?? null}
        onChanged={(actualizado) => void applied(actualizado)}
      />

      <BracketsSection
        tournament={tournament}
        brackets={brackets}
        matches={matches}
        onChanged={applied}
        onMatches={setMatches}
      />

      <TournamentRules tournament={tournament} onChanged={applied} />

      {tournament.canManage && <PublicToggle tournament={tournament} onChanged={applied} />}

      {/* A DÓNDE DEPOSITAR, al lado de lo que cuesta: es la misma pregunta
          —cuánto cobro y dónde me lo pagan— y quien pone el precio es quien
          tiene el número de cuenta en la mano. Ver `AccountsBox`. */}
      {tournament.canManage && (
        <AccountsBox
          accounts={tournament.accounts}
          help={t('tournaments.accountsHelp')}
          onAdd={async (input) => applied(await api.addAccount(tournament.id, input))}
          onRemove={async (accountId) => applied(await api.removeAccount(tournament.id, accountId))}
        />
      )}
      {tournament.canManage && <BrandingSection tournament={tournament} onChanged={applied} />}

      {/* NO se mira el estado del TORNEO: con cada cuadro arrancando por su
          cuenta, un torneo «en juego» puede tener tres categorías esperando.
          La sección decide sola si tiene algo que ofrecer. */}
      {tournament.canManage && (
        <StartSection tournament={tournament} brackets={brackets} onChanged={applied} />
      )}

      {tournament.canManage && (
        <FinishSection tournament={tournament} matches={matches} onChanged={applied} />
      )}

      {/* LA BITÁCORA, AL FINAL DE TODO. Es lo que se lee cuando ya se miró el
          cuadro y las inscripciones, y lo que se escribe después de haber
          hecho algo. Arriba competiría con lo que la gente viene a ver.

          `canManage` es true también para quien fue nombrado organizador de
          ESTE torneo: ver `canManageTournament` en el servicio. El componente
          se esconde solo si el servidor dice que no. */}
      {tournament.canManage && (
        <TournamentNotes
          tournamentId={tournament.id}
          communityId={tournament.communityId}
          puedeNombrar={tournament.canManageCommunity === true}
        />
      )}

      {/*
        BORRAR EL TORNEO, AL FINAL DE TODO Y SEPARADO.

        Es lo único de esta pantalla que no se puede deshacer, así que va
        último: nadie llega acá de paso. Arriba, entre los interruptores,
        estaría al alcance de un toque distraído.

        `canManageCommunity` Y NO `canManage`: quien organiza ESTE torneo puede
        anotar, programar y escribir en la bitácora, pero borrar el torneo
        entero es del CLUB. Es la misma distinción que usa el servidor.
      */}
      {tournament.canManageCommunity === true && (
        <BorrarTorneo tournament={tournament} inscritos={tournament.entries.length} />
      )}
    </AppLayout>
  );
}

/**
 * BORRAR EL TORNEO ENTERO.
 *
 * Javier: «el administrador del club puede borrar un torneo».
 *
 * TRES PASOS Y NO DOS, que es más de lo que pide cualquier otra cosa de la
 * aplicación: primero hay que abrir la sección, después tocar «borrar», y
 * recién ahí confirmar. Se lleva las inscripciones de gente que pagó y no se
 * puede deshacer — vale la pena que cueste.
 *
 * DICE CUÁNTA GENTE HAY ANOTADA, y ese número es la mitad del valor de esta
 * pantalla: «borrar el torneo» y «borrar el torneo con doce inscritos» son dos
 * decisiones distintas, y quien la toma tiene que ver la segunda.
 *
 * NO SE OFRECE SI YA SE JUGÓ ALGO. El servidor lo rechaza igual —es la regla
 * de `borrar-marcador.ts`— pero un botón que siempre falla es peor que no
 * tenerlo: quien lo toca no aprende la regla, aprende que la aplicación se
 * rompe.
 */
function BorrarTorneo({
  tournament,
  inscritos,
}: {
  tournament: Tournament;
  inscritos: number;
}): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * SE ESCONDE EN CUANTO HAY UN PARTIDO JUGADO. `matches` no está acá, así que
   * se mira el estado: un torneo TERMINADO tuvo partidos por definición. Uno
   * «en juego» puede no haber anotado nada todavía, y ahí el servidor decide
   * —la pantalla no puede saberlo sin mirar el cuadro entero—.
   */
  if (tournament.status === 'FINISHED') return null;

  async function borrar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.deleteTournament(tournament.id);
      // A LA LISTA DEL CLUB: quedarse en la pantalla de un torneo que ya no
      // existe deja a la persona mirando datos de algo borrado.
      navigate(`/clubes/${tournament.communityId}/torneos`, { replace: true });
    } catch (caught) {
      setError(errorMessage(t, caught));
      setConfirmando(false);
    } finally {
      setEnviando(false);
    }
  }

  if (!abierto) {
    return (
      <p className="links">
        <button type="button" className="link danger" onClick={() => setAbierto(true)}>
          {t('tournaments.deleteOpen')}
        </button>
      </p>
    );
  }

  return (
    <section className="club-block">
      <div className="club-block-head">
        <h2>{t('tournaments.deleteTitle')}</h2>
      </div>
      {error !== null && <Alert kind="error">{error}</Alert>}
      <p className="help">
        {inscritos > 0
          ? t('tournaments.deleteHelpWithEntries', { count: inscritos })
          : t('tournaments.deleteHelp')}
      </p>
      <div className="button-row">
        {confirmando ? (
          <>
            <button
              type="button"
              className="link danger"
              disabled={enviando}
              onClick={() => void borrar()}
            >
              {enviando ? t('common.sending') : t('tournaments.deleteYes')}
            </button>
            <button
              type="button"
              className="link"
              disabled={enviando}
              onClick={() => setConfirmando(false)}
            >
              {t('common.cancel')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="link danger" onClick={() => setConfirmando(true)}>
              {t('tournaments.deleteOpen')}
            </button>
            <button type="button" className="link" onClick={() => setAbierto(false)}>
              {t('common.cancel')}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * La categoría de alguien EN EL CLUB, para ponerla en la marca.
 *
 * `null` es de quien es miembro y todavía no tiene categoría asignada, que no
 * es lo mismo que no ser del club: son dos estados distintos y la marca tiene
 * que distinguirlos, porque a quien no tiene categoría el club se la puede
 * poner, y a quien no es miembro no hay nada que ponerle.
 */
function nivelLegible(
  t: (clave: string) => string,
  level: CategoryLevel | null,
): string {
  return level === null ? t('members.noLevelYet') : t(`categoryLevel.${level}`);
}

// =====================================================================
// La ficha: el encabezado, y corregirlo
// =====================================================================

/**
 * EL ENCABEZADO DEL TORNEO, Y EL BOTÓN PARA CORREGIRLO.
 *
 * Javier: «Primavera Mujeres debo poder editar el nombre del torneo». Hasta
 * acá el nombre, la descripción, la fecha y los precios se escribían una sola
 * vez, al crear el torneo, y un dedazo solo se arreglaba borrándolo entero —
 * lo que se lleva puestas las inscripciones y el cuadro.
 *
 * EL BOTÓN VA AL LADO DE LO QUE EDITA. Lo que se corrige es exactamente lo
 * que se está leyendo: el nombre grande, la fecha, las dos tarifas. Una
 * sección «Editar torneo» al fondo de la página obligaría a bajar hasta ella
 * y a recordar qué decía arriba.
 *
 * SE PUEDE AUNQUE YA HAYA CUADROS TIRADOS. Un torneo se corre de día y el
 * club sube la cuota; son las dos correcciones que de verdad se hacen, y
 * prohibirlas dejaría al club sin salida justo cuando más lo necesita. Lo ya
 * cobrado no se toca: el precio de una inscripción vieja vive en su fila.
 *
 * LAS CATEGORÍAS NO ESTÁN ACÁ, y no es un olvido: tienen su propia sección,
 * que sabe que un cuadro ya sorteado no se puede sacar.
 */
function BasicsSection({
  tournament,
  onChanged,
}: {
  tournament: Tournament;
  onChanged: (tournament: Tournament) => Promise<void> | void;
}): JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tournament.name);
  const [description, setDescription] = useState(tournament.description ?? '');
  const [startsAt, setStartsAt] = useState(tournament.startsAt);
  const [fee, setFee] = useState<number | null>(tournament.feeColones);
  const [secondFee, setSecondFee] = useState<number | null>(tournament.secondCategoryFeeColones);
  const [abierto, setAbierto] = useState(tournament.openToEveryone);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function abrir(): void {
    // Se recarga del torneo al ABRIR y no al montar: si alguien más lo cambió
    // mientras esta pantalla estaba abierta, el formulario tiene que partir
    // de lo que hay ahora y no de lo que había cuando se cargó la página.
    setName(tournament.name);
    setDescription(tournament.description ?? '');
    setStartsAt(tournament.startsAt);
    setFee(tournament.feeColones);
    setSecondFee(tournament.secondCategoryFeeColones);
    setAbierto(tournament.openToEveryone);
    setError(null);
    setEditing(true);
  }

  const listo =
    name.trim().length >= TOURNAMENT_LIMITS.nameMin && startsAt !== '' && fee !== null;

  async function guardar(): Promise<void> {
    if (!listo) return;
    setSending(true);
    setError(null);
    try {
      await onChanged(
        await api.updateTournament(tournament.id, {
          name: name.trim(),
          // Vacío es `null`, que es «no hay descripción». Guardar `''` sería
          // una descripción que existe y no dice nada: la pantalla le haría
          // lugar a un párrafo en blanco.
          description: description.trim() === '' ? null : description.trim(),
          startsAt,
          feeColones: fee ?? 0,
          // `null` es «lo mismo que la primera», y es distinto de cero, que
          // sería «la segunda es gratis». Por eso no se colapsan.
          secondCategoryFeeColones: secondFee,
          openToEveryone: abierto,
        }),
      );
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (editing) {
    return (
      <section className="card form">
        <h2>{t('tournaments.editTitle')}</h2>

        {error !== null && <Alert kind="error">{error}</Alert>}

        <div className="field">
          <label htmlFor="te-name">{t('tournaments.name')}</label>
          <input
            id="te-name"
            value={name}
            disabled={sending}
            maxLength={TOURNAMENT_LIMITS.nameMax}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="te-date">{t('tournaments.date')}</label>
          <span className="help" id="te-date-help">
            {t('tournaments.dateHelp')}
          </span>
          <input
            id="te-date"
            type="date"
            value={startsAt}
            disabled={sending}
            aria-describedby="te-date-help"
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="te-description">{t('tournaments.description')}</label>
          <textarea
            id="te-description"
            rows={3}
            value={description}
            disabled={sending}
            maxLength={TOURNAMENT_LIMITS.descriptionMax}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="fee-pair">
          <MoneyField
            id="te-fee"
            label={t('tournaments.fee')}
            help={t('tournaments.feeHelp')}
            value={fee}
            disabled={sending}
            onChange={setFee}
          />
          <MoneyField
            id="te-second-fee"
            label={`${t('tournaments.secondFee')} · ${t('common.optional')}`}
            help={t('tournaments.secondFeeHelp')}
            value={secondFee}
            disabled={sending}
            onChange={setSecondFee}
          />
        </div>

        {/* CON GENTE YA INSCRITA, cambiar el precio no le cambia lo que se le
            cobró a nadie: cada inscripción guarda el suyo. Decirlo evita que
            el club no se anime a corregir por miedo a revolver lo cobrado. */}
        {tournament.entries.length > 0 && (
          <p className="help">{t('tournaments.editFeeNote')}</p>
        )}

        {/* EL TORNEO ABIERTO VA ACÁ Y NO AL LADO DE «se ve desde afuera».
            Son dos preguntas distintas —quién lo VE y quién se ANOTA— pero
            ésta se decide mientras se arma el torneo, junto con el precio y
            la fecha, y no desde la vitrina. */}
        <div className="field">
          <label className="check">
            <input
              type="checkbox"
              checked={abierto}
              disabled={sending}
              onChange={(e) => setAbierto(e.target.checked)}
            />
            <span>{t('tournaments.openToggle')}</span>
          </label>
          <p className="help">{t('tournaments.openToggleHelp')}</p>
        </div>

        <div className="button-row">
          <button type="button" onClick={() => void guardar()} disabled={sending || !listo}>
            {sending ? t('common.sending') : t('tournaments.saveBasics')}
          </button>
          <button
            type="button"
            className="link"
            onClick={() => setEditing(false)}
            disabled={sending}
          >
            {t('common.cancel')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="page-heading">
        <h1>{tournament.name}</h1>
      </div>

      {/*
        LA FECHA, CON SU PALABRA. Era una etiqueta suelta entre el estado y
        las dos tarifas, y «11 de octubre» sin nada al lado se lee como un
        dato más de la fila. Decir «Arranca el 11 de octubre» es lo que la
        convierte en la respuesta a la pregunta con la que la gente entra.
      */}
      <p className="community-tags">
        <span className={`tag tag-${tournament.status.toLowerCase()}`}>
          {t(`tournamentStatus.${tournament.status}`)}
        </span>
        <span className="tag tag-when">
          {t('tournaments.startsOn', { date: formatDate(tournament.startsAt) })}
        </span>

        {/*
          UNA SOLA TARIFA, UNA SOLA ETIQUETA.

          Con `?? feeColones` la pantalla mostraba el mismo número dos veces
          —«₡5 000» y «Segunda: ₡5 000»— cuando el club no había puesto
          tarifa propia para la segunda. Se leía como dos precios iguales
          puestos a propósito, y no lo eran: era el hueco rellenado.

          Y cuando sí son dos, cada uno dice de qué categoría es. «₡5 000»
          suelto al lado de «Segunda» no decía que era el de la primera.
        */}
        {tournament.secondCategoryFeeColones === null ? (
          <span className="tag">
            {t('tournaments.feeTag', { amount: formatColones(tournament.feeColones) })}
          </span>
        ) : (
          <>
            <span className="tag">
              {t('tournaments.firstFeeTag', { amount: formatColones(tournament.feeColones) })}
            </span>
            <span className="tag">
              {t('tournaments.secondFeeTag', {
                amount: formatColones(tournament.secondCategoryFeeColones),
              })}
            </span>
          </>
        )}
      </p>

      {tournament.description !== null && <p>{tournament.description}</p>}

      {/*
        EL BOTÓN, EN SU PROPIA FILA.

        Estaba dentro de `.page-heading`, que en el tema clásico se centra y
        se envuelve: caía pegado debajo del nombre, alineado por la línea de
        base de un `h1`. Acá abajo tiene el mismo lugar que todas las demás
        filas de botones de la aplicación.
      */}
      {tournament.canManage && (
        <p className="button-row">
          <button type="button" className="link" onClick={abrir}>
            {t('tournaments.edit')}
          </button>
        </p>
      )}
    </>
  );
}

// =====================================================================
// Terminar el torneo
// =====================================================================

/**
 * DAR EL TORNEO POR TERMINADO, y poder deshacerlo.
 *
 * «En juego» es la etiqueta que le dice a la gente que todavía puede pasar
 * algo. Un torneo que se jugó en marzo y sigue diciendo «en juego» en octubre
 * miente, y quien organiza no tenía cómo callarlo: el botón no existía en
 * ninguna pantalla.
 *
 * ANTES DE CERRAR SE DICE CUÁNTOS PARTIDOS QUEDAN SIN RESULTADO. No para
 * impedirlo —un torneo real se queda sin terminar, y el servidor deja
 * cerrarlo igual— sino para que la decisión se tome sabiendo. «Quedan 3 sin
 * resultado» es lo que hace que alguien se acuerde de que faltaba cargar la
 * final.
 *
 * Y SE PUEDE REABRIR, porque un botón se aprieta sin querer. Sin vuelta
 * atrás, un toque de más deja el torneo sin poder anotar un resultado más.
 */
function FinishSection({
  tournament,
  matches,
  onChanged,
}: {
  tournament: Tournament;
  matches: readonly Match[];
  onChanged: (tournament: Tournament) => Promise<void>;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // En borrador no hay nada que terminar: el servidor lo rechaza y ofrecerlo
  // sería ofrecer un botón que falla.
  if (tournament.status === 'DRAFT') return null;

  /**
   * Los que YA TIENEN LOS DOS LADOS y siguen sin ganador.
   *
   * Un partido de segunda ronda todavía sin rivales no está «sin resultado»:
   * está esperando. Contarlo asustaría con un número que no se puede bajar.
   */
  const pendientes = matches.filter(
    (match) => match.home !== null && match.away !== null && match.winnerEntryId === null,
  ).length;

  async function cambiar(quien: () => Promise<Tournament>): Promise<void> {
    setSending(true);
    setError(null);
    try {
      await onChanged(await quien());
      setConfirming(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <h2>{t('tournaments.finishTitle')}</h2>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {tournament.status === 'FINISHED' ? (
        <>
          <p className="help">{t('tournaments.finishedHelp')}</p>
          <button
            type="button"
            className="secondary"
            disabled={sending}
            onClick={() => void cambiar(() => api.reopenTournament(tournament.id))}
          >
            {sending ? t('common.sending') : t('tournaments.reopen')}
          </button>
        </>
      ) : confirming ? (
        <>
          <Alert kind="info">
            {pendientes > 0
              ? t('tournaments.finishConfirmPending', { count: pendientes })
              : t('tournaments.finishConfirm')}
          </Alert>
          <span className="join-actions">
            <button
              type="button"
              disabled={sending}
              onClick={() => void cambiar(() => api.finishTournament(tournament.id))}
            >
              {sending ? t('common.sending') : t('tournaments.finishConfirmYes')}
            </button>
            <button
              type="button"
              className="link"
              disabled={sending}
              onClick={() => setConfirming(false)}
            >
              {t('common.cancel')}
            </button>
          </span>
        </>
      ) : (
        <>
          <p className="help">{t('tournaments.finishHelp')}</p>
          <button type="button" className="secondary" onClick={() => setConfirming(true)}>
            {t('tournaments.finish')}
          </button>
        </>
      )}
    </section>
  );
}

// =====================================================================
// Las categorías que corre el torneo
// =====================================================================

/**
 * QUÉ CATEGORÍAS CORRE ESTE TORNEO, y poder cambiarlas.
 *
 * Antes se elegían solo al crearlo. Un torneo hecho con puras categorías
 * masculinas no aceptaba ni una jugadora —la lista de categorías al inscribir
 * sale de acá— y la única salida era borrarlo y hacer otro. Javier se topó
 * con eso con doce inscritos adentro.
 *
 * AGREGAR ES LIBRE, incluso con el torneo en juego: cada categoría es su
 * propio cuadro y arranca cuando está lista. QUITAR se traba en las que
 * tienen gente o ya se tiraron; la casilla se ve trabada acá y el servidor lo
 * comprueba igual, porque una casilla no es una regla.
 *
 * SOLO SE OFRECEN LAS DEL CLUB, que es la misma regla de cuando se crea: una
 * categoría que el club no corre no tiene ni un jugador para inscribir. Por
 * eso se pide la ficha del club al abrir el editor — y solo al abrirlo, que
 * es cuando hace falta.
 *
 * LA LISTA LA VE CUALQUIERA; el botón de editar, solo quien administra. Es
 * la segunda pregunta de quien abre un torneo, después de cuándo es.
 */
function TournamentCategoriesSection({
  tournament,
  onChanged,
}: {
  tournament: Tournament;
  onChanged: (tournament: Tournament) => Promise<void> | void;
}): JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CommunityCategory[]>(tournament.categories);
  const [delClub, setDelClub] = useState<CommunityCategory[] | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Las que no se pueden desmarcar: con alguien inscrito, o ya tiradas.
   *
   * Sale de lo que la pantalla ya tiene. El servidor lo comprueba igual y con
   * mejor mensaje —dice cuántos inscritos hay—; esto es para que la casilla
   * no se pueda ni tocar.
   */
  const trabadas = new Set([
    ...tournament.entries.map((e) => categoryKey(e)),
    ...tournament.categories
      .filter((c) => c.startedAt !== null)
      .map((c) => categoryKey(c)),
  ]);

  async function abrir(): Promise<void> {
    setDraft(
      tournament.categories.map((c) => ({
        level: c.level,
        sex: c.sex,
        discipline: c.discipline,
      })),
    );
    setError(null);
    setEditing(true);
    if (delClub !== null) return;
    try {
      const club = await api.findCommunity(tournament.communityId);
      setDelClub(club.categories);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function guardar(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      await onChanged(await api.setTournamentCategories(tournament.id, draft));
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>{t('categories.title')}</h2>
        {tournament.canManage && !editing && (
          <button type="button" className="link" onClick={() => void abrir()}>
            {t('common.edit')}
          </button>
        )}
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {editing ? (
        <>
          <p className="help">{t('tournaments.categoriesHelp')}</p>
          {delClub === null ? (
            <p className="help">{t('common.loading')}</p>
          ) : (
            <CategoryPicker
              value={draft}
              onChange={setDraft}
              disabled={sending}
              lockedKeys={trabadas}
              allowed={delClub}
            />
          )}
          <div className="button-row">
            <button
              type="button"
              onClick={() => void guardar()}
              disabled={sending || delClub === null}
            >
              {sending ? t('common.sending') : t('tournaments.saveCategories')}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => setEditing(false)}
              disabled={sending}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : (
        <p className="community-tags">
          {tournament.categories.map((category) => (
            <span className="tag tag-category" key={categoryKey(category)}>
              {categoryName(t, category)}
            </span>
          ))}
        </p>
      )}
    </section>
  );
}

// =====================================================================
// Inscritos
// =====================================================================

function EntriesSection({
  tournament,
  editable,
  onChanged,
}: {
  tournament: Tournament;
  editable: boolean;
  onChanged: (tournament: Tournament) => Promise<void>;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * QUÉ CUADRO ELIGIÓ QUIEN INSCRIBE, por persona.
   *
   * La LLAVE de la categoría y no el nivel: con los dobles, «B» dejó de
   * nombrar un cuadro —hay B masculino de singles y de dobles— y guardar el
   * nivel es guardar una respuesta que ya no alcanza para la pregunta.
   */
  const [levels, setLevels] = useState<Record<string, string>>({});
  /**
   * CÓMO PAGÓ CADA UNO, por persona de la lista.
   *
   * Por persona y no uno solo para toda la búsqueda: quien organiza está
   * anotando gente que llega a la cancha de a uno, y cada uno llega de una
   * forma distinta — el que ya hizo el SINPE, el que viene por patrocinio, el
   * que todavía no pagó.
   *
   * Arranca sin elegir, y sin elegir significa `ORGANIZER`: lo metió el club.
   * Es lo que de verdad pasó cuando nadie tocó el desplegable.
   */
  const [metodos, setMetodos] = useState<Record<string, EntryPaymentMethod>>({});

  /**
   * QUÉ CATEGORÍA DEL TORNEO SE ESTÁ MIRANDO. `null` = todas.
   *
   * Javier: «filtra también por sexo y categoria sin necesidad de poner el
   * nombre del jugador». Con esto puesto, la búsqueda anda SIN escribir nada:
   * son dos formas de acotar y basta una.
   *
   * NO HAY UN DESPLEGABLE DE SEXO AL LADO, y no es un olvido: una categoría ES
   * nivel × sexo, así que «C · Femenino» ya dice las dos cosas. Un campo de
   * sexo aparte no agregaría un dato — solo dejaría pedir una combinación que
   * el torneo no corre, que es exactamente el callejón que tenía la caja de
   * invitado.
   *
   * SOLO LAS ABIERTAS: en una ya sorteada no se puede anotar a nadie, así que
   * ofrecerla sería ofrecer una lista para no hacer nada con ella.
   */
  const abiertas = tournament.categories.filter((c) => c.startedAt === null);
  const [filtro, setFiltro] = useState<string | null>(null);
  const elegida = abiertas.find((c) => categoryKey(c) === filtro) ?? null;

  /*
   * LA CLAVE Y NO EL OBJETO. El hook la compara por identidad para decidir si
   * volver a buscar: un objeto nuevo en cada dibujo buscaría en cada dibujo.
   */
  const busqueda = usePlayerSearch(
    tournament.communityId,
    query,
    elegida === null ? null : categoryKey(elegida),
  );

  /**
   * La página de resultados que se ve.
   *
   * Con una categoría puesta y sin nombre son cincuenta personas: la lista
   * entera dibujada de una empuja la caja de invitado —y todo lo de abajo— a
   * pantallas de distancia. Se reinicia al cambiar lo buscado; si no, alguien
   * que estaba en la página 3 y afina la búsqueda cae en una página vacía.
   */
  const pagina = usePaginado(busqueda.results, `${query}|${filtro ?? ''}`, 10);

  /**
   * Cuántas categorías tomó ya cada persona en ESTE torneo.
   *
   * Se cuenta acá para poder apagar el botón, pero quien decide es el
   * servidor: esto es para no ofrecer algo que va a rebotar.
   */
  const tomadas = new Map<string, number>();
  for (const entry of tournament.entries) {
    // Los invitados no entran: este mapa contesta «¿cuántas categorías tomó
    // ESTA persona?» para apagar las que ya tiene, y a un invitado no se lo
    // busca por la lista de jugadores. Con `null` de clave, todos los
    // invitados del torneo taparían las categorías de una misma persona
    // inexistente.
    if (entry.userId === null) continue;
    tomadas.set(entry.userId, (tomadas.get(entry.userId) ?? 0) + 1);
  }

  async function inscribir(
    player: PlayerSearchResult,
    categoria: CommunityCategory,
    partnerUserId: string | null,
  ): Promise<void> {
    setError(null);
    try {
      await onChanged(
        await api.addTournamentEntry(
          tournament.id,
          player.userId,
          categoria,
          partnerUserId,
          metodos[player.userId],
        ),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /**
   * SIN NADA QUE MOSTRAR, NO HAY SECCIÓN.
   *
   * Esta caja es SOLO para agregar gente. Quien no administra no ve nada
   * acá, y antes veía un título con un contador debajo — el mismo contador
   * que ya está en la nómina, dos renglones más abajo. Dos veces «Inscritos»
   * en la misma pantalla hace dudar de cuál de los dos es el bueno.
   */
  if (!editable) return null;

  return (
    <section>
      <div className="section-head">
        <h2>{t('tournaments.addEntry')}</h2>
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="add-member">
          {/* DECÍA «Solo gente del club», Y NO ERA CIERTO: `addEntry` acepta
              a quien no es miembro desde que Javier lo pidió —a un torneo
              llega gente de otros clubes—. Un texto que promete menos de lo
              que el sistema hace es tan caro como uno que promete de más:
              Javier le creyó y dio por perdido buscar a nadie de afuera. */}
          <p className="help">{t('tournaments.addEntryHelp')}</p>

          <div className="field">
            <label htmlFor="entry-search">{t('members.searchLabel')}</label>
            <input
              id="entry-search"
              type="search"
              value={query}
              autoComplete="off"
              placeholder={t('members.searchPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
              aria-describedby="entry-search-status"
            />
          </div>

          {/*
            EL FILTRO POR CATEGORÍA, que sirve SOLO o junto al nombre.

            Va DEBAJO del buscador y no arriba porque lo primero que alguien
            intenta es escribir un nombre; el filtro es la otra forma, para
            cuando no se sabe a quién se busca y hay que mirar quiénes hay.
          */}
          {abiertas.length > 0 && (
            <div className="field">
              <label htmlFor="entry-can-play">{t('members.canPlayLabel')}</label>
              <Select<string>
                id="entry-can-play"
                value={filtro ?? ''}
                options={[
                  { value: '', label: t('members.canPlayAny') },
                  ...abiertas.map((c) => ({
                    value: categoryKey(c),
                    label: categoryName(t, c),
                  })),
                ]}
                onChange={(v) => setFiltro(v === '' ? null : v)}
              />
              <p className="help">{t('members.canPlayHelp')}</p>
            </div>
          )}

          <p className="help" id="entry-search-status" role="status">
            {busqueda.tooShort
              ? t('members.keepTyping', { min: MIN_SEARCH_LENGTH })
              : busqueda.searching
                ? t('members.searching')
                : query.trim().length < MIN_SEARCH_LENGTH && elegida === null
                  ? // Sin nada que acotar no se buscó nada, y no hay nada que
                    // contar. Callar es mejor que decir «0 resultados», que
                    // se leería como «no existe esa persona».
                    ''
                  : busqueda.results.length === 0
                    ? t('members.none')
                    : busqueda.hasMore
                      ? // HUBO MÁS DE LOS QUE CABEN. Decirlo es lo que
                        // distingue «no está en la aplicación» de «quedó
                        // abajo del corte», que desde afuera se ven igual.
                        t('members.foundMore', { n: busqueda.results.length })
                      : t('members.found', { n: busqueda.results.length })}
          </p>

          {busqueda.error !== null && <Alert kind="error">{errorMessage(t, busqueda.error)}</Alert>}

          <ul className="member-list">
            {pagina.visibles.map((player) => {
              /**
               * EN QUÉ CATEGORÍAS SE PUEDE ANOTAR ESA PERSONA.
               *
               * LA MISMA FUNCIÓN QUE USA TODO LO DEMÁS, y esa es toda la
               * corrección. Acá había una copia escrita a mano de las cinco
               * condiciones, y le faltaban dos: que el cuadro no se haya
               * tirado, y que el torneo no esté terminado.
               *
               * Con la categoría A ya sorteada, esa categoría seguía
               * apareciendo en el desplegable y encima salía elegida por
               * defecto. Quien organiza apretaba «Agregar» sin tocar nada y
               * el servidor contestaba que ese cuadro ya empezó — que desde
               * afuera se lee como «no me deja inscribir a nadie».
               *
               * `level` es la categoría de ESA persona en el club, no la de
               * quien mira: es la diferencia entre «¿en qué me anoto yo?» y
               * «¿en qué la anoto a ella?», y es la razón por la que existía
               * la copia.
               */
              const quien = {
                id: player.userId,
                sex: player.sex,
                level: player.level,
              };
              /*
               * SOLO SINGLES DESDE ACÁ, POR AHORA.
               *
               * Esta caja es una fila por persona con un desplegable y un
               * botón, y una inscripción de dobles necesita DOS personas. La
               * pantalla para armar la pareja existe en la caja de anotarse
               * uno mismo, donde hay lugar para buscarla; meterla en una fila
               * de resultados de búsqueda sería un buscador adentro de otro.
               *
               * Está escrito y no deducido: sin este filtro, el desplegable
               * ofrecería los cuadros de dobles y el botón mandaría una
               * inscripción sin pareja, que el servidor rechaza SIEMPRE. Es
               * mejor no ofrecer lo que no se puede hacer que ofrecerlo y
               * fallar.
               */
              const disponibles = enterableCategories(tournament, quien).filter(
                (c) => c.discipline === 'SINGLES',
              );
              /*
               * Y SI NO HAY NINGUNA, POR QUÉ.
               *
               * Antes la pantalla decía siempre lo mismo —«este torneo no
               * corre ninguna categoría de C»— hubiera el motivo que hubiera,
               * y son seis. Cuando el motivo era otro, esa frase no era
               * incompleta: era falsa, y mandaba a buscar el problema donde
               * no estaba. El caso más común es el que más tapaba: el cuadro
               * ya se sorteó.
               */
              const motivo = whyNotEnterable(tournament, quien);
              // POR LLAVE Y NO POR NIVEL: con los dobles, «B» ya no nombra un
              // cuadro. Acá arriba se filtró a singles, así que hoy no hay dos
              // con el mismo nivel — pero guardar el nivel sería dejar puesta
              // la ambigüedad para el día que se quite el filtro.
              const elegida =
                disponibles.find((c) => categoryKey(c) === levels[player.userId]) ??
                disponibles[0];
              // Se sigue contando para saber si esta es la SEGUNDA —que paga
              // distinto y por eso el botón cambia de texto—; quién NO puede
              // anotarse lo decide `motivo`.
              const yaTomo = tomadas.get(player.userId) ?? 0;

              return (
                <li key={player.userId}>
                  <Avatar url={assetUrl(player.avatarPath)} name={player.displayName} size={40} />
                  <div className="member-name">
                    <strong>{player.displayName}</strong>
                    <span className="help">{player.fullName}</span>
                    {/*
                      SI ES DEL CLUB O NO, dicho en cada fila.

                      La búsqueda siempre trajo a todo el mundo y la API
                      siempre aceptó inscribir a quien no es del club: a un
                      torneo llega gente de otros lados. Lo que faltaba era
                      decirlo. Sin la marca, quien organiza no sabe si la
                      «Ana Jiménez» que encontró es la de su club o una
                      homónima de otro, y el texto de ayuda —que decía «solo
                      gente del club»— lo dejaba creyendo que ni podía.

                      VA PEGADA AL NOMBRE y no suelta en la fila: es un dato
                      SOBRE ESA PERSONA, como su nombre completo, y no una
                      condición de la inscripción como las de la derecha.
                    */}
                    <span className={player.alreadyMember ? 'tag tag-ok' : 'tag'}>
                      {player.alreadyMember
                        ? t('members.inClub', { level: nivelLegible(t, player.level) })
                        : t('members.outsideClub')}
                    </span>
                  </div>

                  {motivo !== null ? (
                    // UNA FRASE POR MOTIVO. El nivel va de dato porque la
                    // única que lo usa es la de «ninguna de su nivel», y ahí
                    // es lo que hay que leer para entenderla.
                    <span className="tag">
                      {t(`cantEnter.${motivo}`, {
                        level: player.level === null ? '' : t(`categoryLevel.${player.level}`),
                      })}
                    </span>
                  ) : (
                    <>
                      <Select<string>
                        id={`entry-level-${player.userId}`}
                        aria-label={t('members.categoryOf', { name: player.displayName })}
                        // `disponibles` no está vacío: lo comprueba la rama
                        // de arriba, así que el primero siempre existe.
                        value={categoryKey(elegida ?? disponibles[0]!)}
                        options={disponibles.map((category) => ({
                          value: categoryKey(category),
                          label: t(`categoryLevel.${category.level}`),
                        }))}
                        onChange={(clave) =>
                          setLevels((actual) => ({ ...actual, [player.userId]: clave }))
                        }
                      />
                      <MetodoPicker
                        id={`entry-method-${player.userId}`}
                        name={player.displayName}
                        value={metodos[player.userId]}
                        onChange={(m) =>
                          setMetodos((actual) => ({ ...actual, [player.userId]: m }))
                        }
                      />
                      <button
                        type="button"
                        disabled={elegida === undefined}
                        /*
                          A QUIÉN. En la fila dice «Agregar» porque es lo
                          único que cabe, pero hay uno por resultado y un
                          lector de pantalla los anuncia a los tres igual.
                          El nombre accesible CONTIENE el texto visible, que
                          es lo que necesita quien maneja la app por voz para
                          poder decir «Agregar» y que le haga caso.
                        */
                        aria-label={
                          yaTomo === 1
                            ? t('tournaments.addSecondTo', { name: player.displayName })
                            : t('members.addTo', { name: player.displayName })
                        }
                        onClick={() => {
                          // `null` de pareja: acá solo hay singles, ver el
                          // filtro de `disponibles`.
                          if (elegida !== undefined) void inscribir(player, elegida, null);
                        }}
                      >
                        {/* Si ya tomó una, esta es la segunda y paga distinto. */}
                        {yaTomo === 1 ? t('tournaments.addSecond') : t('members.add')}
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />

          <GuestBox tournament={tournament} onChanged={onChanged} />
        </div>
    </section>
  );
}

/**
 * CÓMO PAGÓ: el desplegable que Javier pidió.
 *
 * SIN ELEGIR ES UNA OPCIÓN DE VERDAD y es la primera: quien anota a alguien
 * que llegó a la cancha muchas veces todavía no le cobró, y obligarlo a
 * declarar un medio que no existe lo empujaría a poner cualquiera. Sin elegir
 * el servidor guarda `ORGANIZER`, que es exactamente lo que pasó.
 */
function MetodoPicker({
  id,
  name,
  value,
  onChange,
}: {
  id: string;
  name: string;
  value: EntryPaymentMethod | undefined;
  onChange: (method: EntryPaymentMethod) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Select<EntryPaymentMethod>
      id={id}
      aria-label={t('entries.howPaidOf', { name })}
      value={value ?? 'ORGANIZER'}
      options={ENTRY_PAYMENT_METHODS.map((method) => ({
        value: method,
        label: t(`paymentMethod.${method}`),
      }))}
      onChange={onChange}
    />
  );
}

// =====================================================================
// Cuadros
// =====================================================================

function BracketsSection({
  tournament,
  brackets,
  matches,
  onChanged,
  onMatches,
}: {
  tournament: Tournament;
  brackets: readonly CategoryBracket[];
  matches: readonly Match[];
  // `editable` NO está: lo que se puede tocar acá se decide POR CUADRO, con
  // el `startedAt` de cada categoría. Ver adentro del map.
  onChanged: (tournament: Tournament) => Promise<void>;
  onMatches: (matches: Match[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  // Qué partido se está anotando. Uno a la vez: abrir dos editores sobre el
  // mismo cuadro es cómo se guarda un resultado encima de otro.
  const [anotando, setAnotando] = useState<Match | null>(null);
  // Y cuál se está programando. Uno a la vez, por lo mismo.
  const [programando, setProgramando] = useState<Match | null>(null);
  /*
   * LA AGENDA LLEVA SU PROPIO PAR, y no comparte el de los cuadros.
   *
   * Es el mismo partido y el mismo editor, pero abierto desde otro lado, y
   * eso decide DÓNDE tiene que aparecer: quien toca «Poner fecha» en la
   * agenda de abajo espera que el formulario se abra ahí, debajo de ese
   * renglón. Con un solo estado compartido se abría además allá arriba, en la
   * categoría —fuera de la pantalla— y quedaban dos formularios vivos sobre
   * el mismo partido.
   */
  const [enAgendaAnotando, setEnAgendaAnotando] = useState<Match | null>(null);
  const [enAgendaProgramando, setEnAgendaProgramando] = useState<Match | null>(null);

  // Terminado el torneo ya no se anota nada, sea cual sea el cuadro.
  const cerrado = tournament.status === 'FINISHED';

  /**
   * BORRAR EL MARCADOR de un partido del cuadro.
   *
   * POR SU PROPIA RUTA, no mandando un marcador vacío por la de anotar: ahí el
   * permiso es el de ANOTAR, y borrar tiene que poderlo menos gente. El
   * servidor devuelve TODOS los partidos, igual que al anotar, porque borrar
   * puede sacar a alguien de la ronda siguiente.
   */
  async function borrarMarcador(match: Match): Promise<void> {
    const actualizados = await api.clearMatchScore(tournament.id, match.id);
    onMatches(actualizados);
    setAnotando(null);
    setEnAgendaAnotando(null);
  }

  async function guardarMarcador(
    match: Match,
    score: MatchScore,
    noShowEntryId: string | null,
  ): Promise<void> {
    const actualizados = await api.saveMatchScore(tournament.id, match.id, score, noShowEntryId);
    onMatches(actualizados);
    setAnotando(null);
    setEnAgendaAnotando(null);
  }

  /**
   * LA FOTO DEL PARTIDO. Va aparte del marcador: son dos cosas distintas.
   *
   * El servidor devuelve TODOS los partidos, igual que al anotar, y se
   * guardan todos: es más barato que reconstruir uno solo a mano y no puede
   * quedar desincronizado.
   */
  async function guardarFoto(match: Match, file: File | null): Promise<void> {
    onMatches(
      file === null
        ? await api.removeMatchPhoto(tournament.id, match.id)
        : await api.setMatchPhoto(tournament.id, match.id, file),
    );
  }

  /**
   * Guardar la programación de un partido.
   *
   * El servidor devuelve TODOS los partidos y no solo el que cambió: con
   * «repetir en todas» marcado cambian los demás, y quedarse con uno dejaría
   * la pantalla mostrando lo viejo en el resto del cuadro.
   */
  async function programar(
    match: Match,
    schedule: MatchSchedule,
    repetir: boolean,
  ): Promise<void> {
    setError(null);
    try {
      onMatches(
        await api.setMatchSchedule(tournament.id, match.id, { ...schedule, applyPlaceToAll: repetir }),
      );
      setProgramando(null);
      setEnAgendaProgramando(null);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /** La fecha límite de una ronda. Recarga la ficha: vive en el torneo. */
  async function ponerLimite(
    category: CommunityCategory,
    players: number,
    date: string | null,
  ): Promise<void> {
    setError(null);
    try {
      await onChanged(
        await api.setRoundDeadline(tournament.id, {
          level: category.level,
          sex: category.sex,
          discipline: category.discipline,
          round: players,
          deadline: date,
        }),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  if (tournament.categories.length === 0) {
    return (
      <section>
        <h2>{t('bracket.title')}</h2>
        <p className="help">{t('tournaments.noCategories')}</p>
      </section>
    );
  }

  /**
   * A QUIÉN SE TOCÓ PRIMERO, esperando con quién cambiarse.
   *
   * Uno solo para toda la sección y no uno por categoría: no se puede estar
   * cambiando gente en dos cuadros a la vez, y con uno por categoría habría
   * que acordarse de limpiarlos todos.
   */
  const [elegido, setElegido] = useState<string | null>(null);

  /**
   * TOCAR UN JUGADOR DEL BORRADOR.
   *
   * El primer toque lo marca. El segundo, sobre OTRO, los intercambia; sobre
   * el MISMO, lo desmarca —que es cómo se cancela sin tener que buscar un
   * botón de cancelar—.
   *
   * El intercambio es de SIEMBRAS: la posición en el cuadro sale del orden de
   * siembra, así que cambiarlos de lugar en el árbol y cambiarles el número
   * son la misma cosa. Por eso no hace falta una operación nueva en la API.
   */
  async function tocar(category: CommunityCategory, entryId: string): Promise<void> {
    if (elegido === null) {
      setElegido(entryId);
      return;
    }
    if (elegido === entryId) {
      setElegido(null);
      return;
    }

    const orden = tournament.entries
      .filter((e) => categoryKey(e) === categoryKey(category))
      .sort((a, b) => a.seed - b.seed)
      .map((e) => e.id);

    const uno = orden.indexOf(elegido);
    const otro = orden.indexOf(entryId);
    // Si alguno no está en ESTA categoría, no hay nada que intercambiar: se
    // tocó en un cuadro y después en otro. Se empieza de nuevo desde el
    // segundo, que es lo que la persona acaba de señalar.
    if (uno === -1 || otro === -1) {
      setElegido(entryId);
      return;
    }

    orden[uno] = entryId;
    orden[otro] = elegido;
    setElegido(null);
    await guardarSiembra(category, orden);
  }

  async function guardarSiembra(
    category: CommunityCategory,
    entryIdsInOrder: string[],
  ): Promise<void> {
    setError(null);
    try {
      await onChanged(
        await api.reseedTournament(tournament.id, category, entryIdsInOrder),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function sortear(category: CommunityCategory): Promise<void> {
    setError(null);
    try {
      await onChanged(await api.drawTournament(tournament.id, category));
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function quitar(entryId: string): Promise<void> {
    setError(null);
    try {
      await onChanged(await api.removeTournamentEntry(tournament.id, entryId));
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  // Hay algo que agrandar cuando al menos un cuadro ya se tiró: antes de eso
  // es una previsión, y una previsión mirada en grande se lee como
  // definitiva. Ver `BracketFullPage`.
  const hayCuadroTirado = tournament.categories.some((c) => c.startedAt !== null);

  return (
    <section>
      <div className="section-head">
        <h2>{t('bracket.title')}</h2>
        {/* VER EL CUADRO EN GRANDE. Acá adentro le llegan unos 300 px de los
            390 de la pantalla —la tarjeta tiene bordes, relleno y un ancho
            máximo—, así que un cuadro de dieciséis se mira de a dos rondas y
            media. */}
        {hayCuadroTirado && (
          <Link className="button-like" to={`/torneos/${tournament.id}/cuadro`}>
            {t('bracket.openFull')}
          </Link>
        )}
      </div>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {tournament.categories.map((category) => {
        const bracket = brackets.find((b) => categoryKey(b) === categoryKey(category));
        const delCuadro = matches.filter(
          (m) => categoryKey(m) === categoryKey(category),
        );
        const inscritos: TournamentEntry[] = tournament.entries
          .filter((e) => categoryKey(e) === categoryKey(category))
          .sort((a, b) => a.seed - b.seed);

        /**
         * TODO ESTO ES POR CUADRO, no por torneo.
         *
         * Con cada categoría arrancando por su cuenta, el estado del torneo
         * dejó de servir para decidirlo: un torneo «en juego» porque el A
         * masculino ya se tiró tiene al C2 femenino todavía en borrador, y
         * mirarle el estado al torneo cerraría el C2 sin motivo.
         */
        const tirado = category.startedAt !== null;
        // Se acomoda y se sortea mientras el cuadro NO se tiró.
        const acomodable = tournament.canManage && !tirado;
        /**
         * QUÉ PARTIDOS SE PUEDEN ANOTAR ya no se decide acá: viene en
         * `match.canScore`, partido por partido. Un jugador puede anotar el
         * suyo y ninguno más —si el club lo permite—, y eso no es una
         * respuesta que valga para toda la categoría.
         *
         * Lo que sí se decide acá es si HAY algo que anotar: el cuadro tirado
         * y el torneo sin terminar. Con eso se ofrece el editor, y cada caja
         * de partido decide si se puede tocar.
         */
        const anotable = tirado && !cerrado;
        // Programar y poner fechas límite SIGUE SIENDO de quien organiza: el
        // servidor lo exige, y ofrecer un botón que va a dar 403 es peor que
        // no ofrecerlo.
        const programable = tournament.canManage && anotable;
        // El cuadro se ve cuando ya se tiró —ahí es un hecho— o cuando quien
        // mira es quien lo está armando.
        const verElCuadro = tirado || tournament.canManage;
        // Las tablas se dibujan aparte: un árbol no dice en qué lugar va cada
        // quien, que es justamente lo que se mira en un todos contra todos.
        const porTablas = category.format !== 'KNOCKOUT';

        /* Los editores son los mismos para las llaves y para las tablas: un
           partido se anota igual esté donde esté. */
        const editores = (
          <>
            {programando !== null &&
              categoryKey(programando) === categoryKey(category) && (
                <ScheduleEditor
                  match={programando}
                  onCancel={() => setProgramando(null)}
                  onSave={(schedule, repetir) => programar(programando, schedule, repetir)}
                />
              )}
            {anotando !== null &&
              categoryKey(anotando) === categoryKey(category) && (
                <ScoreEditor
                  {...sidesOfMatch(anotando)}
                  onCancel={() => setAnotando(null)}
                  onSave={(score, noShow) => guardarMarcador(anotando, score, noShow)}
                  onPhoto={(file) => guardarFoto(anotando, file)}
                  {...(anotando.canClearScore === true
                    ? { onClear: () => borrarMarcador(anotando) }
                    : {})}
                />
              )}
          </>
        );

        return (
          <article className="bracket-category" key={categoryKey(category)}>
            <div className="section-head">
              <h3>
                {categoryName(t, category)}
              </h3>
              <span className="tag">{t('tournaments.entryCount', { n: inscritos.length })}</span>
            </div>

            {/*
              EL CUADRO EN BORRADOR SOLO LO VE QUIEN ORGANIZA.

              Antes de tirar el torneo el cuadro es una PREVISIÓN: cambia con
              cada inscripción y con cada sorteo. Publicarlo hace que la gente
              lo lea como definitivo, se organice según él, y después reclame
              cuando cambia. Quien organiza sí lo necesita —es lo que está
              armando—, así que lo ve.

              Lo que sí ve todo el mundo es QUIÉNES ESTÁN INSCRITOS, que es lo
              que de verdad se viene a mirar antes de un torneo.
            */}
            {!verElCuadro ? (
              <EntryList
                entries={inscritos}
                de={categoryName(t, category)}
              />
            ) : porTablas ? (
              <>
                <p className="help">{t(`format.help.${category.format}`)}</p>
                {/*
                  LAS REGLAS DE LAS FINALS, al lado de la tabla que gobiernan.
                  Es el único formato cuyo desempate no es el de siempre: sin
                  esto, quien ve segundo a quien le ganó a todos no tiene dónde
                  enterarse de por qué. Plegado: acá se viene a ver la tabla.
                */}
                {category.format === 'FINALS' && (
                  <FinalsRules
                    first={category.inviteDaysFirst}
                    second={category.inviteDaysSecond}
                    rest={category.inviteDaysRest}
                  />
                )}
                {editores}
                {tirado ? (
                  <Groups
                    format={category.format}
                    tournamentName={tournament.name}
                    matches={delCuadro}
                    entries={inscritos}
                    {...(anotable ? { onScore: (match: Match) => setAnotando(match) } : {})}
                    {...(programable ? { onSchedule: (match: Match) => setProgramando(match) } : {})}
                    deadlines={tournament.roundDeadlines.filter(
                      (d) => categoryKey(d) === categoryKey(category),
                    )}
                    {...(programable
                      ? {
                          onDeadline: (players: number, date: string | null) =>
                            void ponerLimite(category, players, date),
                        }
                      : {})}
                  />
                ) : (
                  /* Antes de tirar no hay TABLA que mostrar —no se jugó
                     nada—, pero sí se puede mostrar el reparto: es lo único
                     que todavía se puede corregir. */
                  <GroupPreview format={category.format} entries={inscritos} />
                )}
              </>
            ) : bracket === undefined || bracket.problem !== null ? (
              <p className="help">{t('bracket.notPossible')}</p>
            ) : (
              <>
                <p className="help">
                  {t('bracket.sizeAndByes', { size: bracket.size, byes: bracket.byes })}
                </p>
                {/*
                  MIENTRAS NO SE TIRA, ES UN BORRADOR Y SE DICE.

                  Sin el cartel, el cuadro del borrador y el definitivo se ven
                  idénticos, y la diferencia es enorme: uno se puede rehacer y
                  el otro no. Quien administra tiene que saber cuál está
                  mirando ANTES de mandarle el enlace a nadie.
                */}
                {acomodable && (
                  <Alert kind="info">
                    {inscritos.length > 1 ? t('draw.draftPickHelp') : t('draw.draftHelp')}
                  </Alert>
                )}
                {editores}
                <Bracket
                  matches={delCuadro}
                  tournamentName={tournament.name}
                  deadlines={tournament.roundDeadlines.filter(
                    (d) => categoryKey(d) === categoryKey(category),
                  )}
                  {...(anotable ? { onScore: (match: Match) => setAnotando(match) } : {})}
                  {...(acomodable || programable
                    ? {
                        onDeadline: (players: number, date: string | null) =>
                          void ponerLimite(category, players, date),
                      }
                    : {})}
                  {...(programable ? { onSchedule: (match: Match) => setProgramando(match) } : {})}
                  {...(acomodable
                    ? // Tocar para mover SOLO en el borrador: con el cuadro
                      // tirado hay resultados jugados contra ese orden.
                      {
                        onPick: (entryId: string) => void tocar(category, entryId),
                        elegido,
                      }
                    : {})}
                />
              </>
            )}

            {acomodable && inscritos.length > 0 && (
              <div className="button-row">
                {/* En una tabla el sorteo NO arma un cuadro: reparte los
                    grupos. Decir «las esquinas» ahí sería describir algo que
                    no existe en ese formato. */}
                <button type="button" onClick={() => void sortear(category)}>
                  {porTablas ? t('draw.actionGroups') : t('draw.action')}
                </button>
                <span className="help">{porTablas ? t('draw.helpGroups') : t('draw.help')}</span>
              </div>
            )}

            {acomodable && inscritos.length > 0 && (
              <SeedList
                entries={inscritos}
                onSave={(orden) => guardarSiembra(category, orden)}
                onRemove={quitar}
              />
            )}
          </article>
        );
      })}

      {/*
        LA AGENDA, UNA SOLA Y DEBAJO DE TODAS LAS CATEGORÍAS.

        Es donde se reparten canchas y fechas. Junta las categorías a
        propósito: quien organiza no programa «el cuadro A femenino», programa
        EL SÁBADO —tres canchas, seis horarios, los partidos que haya— y para
        eso los necesita en una sola lista ordenada por cuándo se juegan.

        Y ES LO QUE LES FALTABA A LAS TABLAS. Un todos contra todos no se
        dibuja como cuadro, así que hasta acá no existía ningún lugar donde
        ponerle cancha a un partido de grupo: los partidos estaban repartidos
        por jornada adentro de cada tabla, sin más botón que el marcador.
      */}
      {matches.length > 0 && (
        <NextMatches
          matches={matches}
          deadlines={tournament.roundDeadlines}
          {...(cerrado ? {} : { onScore: setEnAgendaAnotando })}
          {...(tournament.canManage && !cerrado
            ? { onSchedule: setEnAgendaProgramando }
            : {})}
          debajo={(match) => (
            <>
              {enAgendaProgramando?.id === match.id && (
                <ScheduleEditor
                  match={enAgendaProgramando}
                  onCancel={() => setEnAgendaProgramando(null)}
                  onSave={(schedule, repetir) =>
                    programar(enAgendaProgramando, schedule, repetir)
                  }
                />
              )}
              {enAgendaAnotando?.id === match.id && (
                <ScoreEditor
                  {...sidesOfMatch(enAgendaAnotando)}
                  onCancel={() => setEnAgendaAnotando(null)}
                  onSave={(score, noShow) => guardarMarcador(enAgendaAnotando, score, noShow)}
                  onPhoto={(file) => guardarFoto(enAgendaAnotando, file)}
                />
              )}
            </>
          )}
        />
      )}
    </section>
  );
}

/**
 * INSCRIBIR A ALGUIEN QUE NO TIENE LA APLICACIÓN.
 *
 * El caso es el del sábado a las ocho: llega un jugador de otro club, quiere
 * jugar, y no tiene cuenta. Hasta acá la única salida era inventarle un correo
 * y crearle una cuenta que esa persona nunca iba a poder recuperar —y que
 * después aparece en las búsquedas de retos de todo el mundo—.
 *
 * VA DEBAJO DE LA BÚSQUEDA Y NO EN UNA PESTAÑA APARTE. El orden es el de la
 * cabeza de quien organiza: primero se busca a la persona, y solo cuando no
 * aparece se la escribe a mano. Una pestaña obligaría a decidir antes de
 * buscar, que es justo al revés.
 *
 * PLEGADA POR DEFECTO, porque es la excepción: la mayoría de la gente sí está
 * en la lista, y un formulario de tres campos abierto todo el tiempo compite
 * con la búsqueda que casi siempre es la respuesta correcta.
 */
function GuestBox({
  tournament,
  onChanged,
}: {
  tournament: Tournament;
  onChanged: (tournament: Tournament) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [abierta, setAbierta] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [clave, setClave] = useState<string | null>(null);
  const [metodo, setMetodo] = useState<EntryPaymentMethod>('ORGANIZER');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  /**
   * LAS CATEGORÍAS ABIERTAS DEL TORNEO, LAS DOS SEXOS JUNTAS.
   *
   * Antes esta caja pedía el SEXO en su propio desplegable y recién después
   * ofrecía la categoría, filtrada por él. Con «Masculino» puesto en un
   * torneo que solo corre C femenino, el campo de categoría DESAPARECÍA y
   * quedaba una frase suelta —«no queda ninguna categoría sin sortear para
   * ese sexo»— sin nada que dijera que el problema estaba en el campo de
   * arriba. Javier mandó la foto de ese callejón.
   *
   * Una categoría ES nivel × sexo, así que elegir «C · Femenino» ya dice las
   * dos cosas: el campo de sexo no aportaba un dato, solo permitía llegar a
   * una combinación que el torneo no corre. Con las categorías de verdad
   * listadas, eso no se puede ni escribir.
   *
   * Las YA SORTEADAS quedan afuera: meter a alguien en un cuadro tirado no es
   * inscribirlo.
   */
  const disponibles = tournament.categories.filter((c) => c.startedAt === null);
  const elegida =
    disponibles.find((c) => categoryKey(c) === clave) ?? disponibles[0];

  async function guardar(): Promise<void> {
    if (elegida === undefined) return;
    setError(null);
    setSending(true);
    try {
      await onChanged(
        await api.addGuestEntry(tournament.id, {
          name: name.trim(),
          /*
            * EL SEXO SALE DE LA CATEGORÍA, así no hay forma de mandar a alguien
            * a un cuadro que el torneo no corre.
            *
            * EN UN MIXTO NO SE PUEDE DEDUCIR: el cuadro es `MIXED` y adentro
            * juegan un hombre y una mujer, así que hay que preguntarlo. Por
            * ahora los invitados solo entran a los cuadros de un solo sexo —el
            * formulario de dobles todavía no existe— y el `?? 'MALE'` nunca se
            * alcanza: cuando exista, este es el lugar que hay que tocar.
            */
          sex: elegida.sex === 'MIXED' ? 'MALE' : elegida.sex,
          // Vacío es «no dejó teléfono», no «su teléfono es la cadena vacía».
          ...(phone.trim() === '' ? {} : { whatsappPhone: phone.trim() }),
          level: elegida.level,
          paymentMethod: metodo,
        }),
      );
      // Se limpia SOLO si salió bien: con un error, lo escrito se queda para
      // poder corregirlo en vez de tener que volver a teclearlo todo.
      setName('');
      setPhone('');
      setAbierta(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (!abierta) {
    return (
      <p className="guest-open">
        <button type="button" className="link" onClick={() => setAbierta(true)}>
          {t('tournaments.guestOpen')}
        </button>
      </p>
    );
  }

  return (
    <div className="guest-box">
      <h3>{t('tournaments.guestTitle')}</h3>
      <p className="help">{t('tournaments.guestHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor="guest-name">{t('tournaments.guestName')}</label>
        <input
          id="guest-name"
          value={name}
          maxLength={60}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="guest-phone">{t('tournaments.guestPhone')}</label>
        <input
          id="guest-phone"
          type="tel"
          value={phone}
          autoComplete="off"
          placeholder="+506 8888 7777"
          onChange={(e) => setPhone(e.target.value)}
          aria-describedby="guest-phone-help"
        />
        <p className="help" id="guest-phone-help">
          {t('tournaments.guestPhoneHelp')}
        </p>
      </div>

      {disponibles.length === 0 ? (
        // Sin NINGUNA categoría abierta en todo el torneo no hay dónde
        // ponerlo, y decirlo es mejor que dejar un botón que siempre rebota.
        // Ahora es un hecho del torneo entero y no del campo de arriba.
        <p className="help">{t('tournaments.guestNoCategory')}</p>
      ) : (
        <div className="field">
          <label htmlFor="guest-level">{t('ranking.category')}</label>
          <Select<string>
            id="guest-level"
            value={categoryKey(elegida!)}
            options={disponibles.map((c) => ({
              value: categoryKey(c),
              label: categoryName(t, c),
            }))}
            onChange={setClave}
          />
        </div>
      )}

      {/* CÓMO PAGÓ, también para el invitado. Es el caso donde más se usa: el
          que llega a la cancha sin la aplicación es justo el que paga en la
          mano o viene por patrocinio. */}
      <div className="field">
        <label htmlFor="guest-method">{t('entries.howPaid')}</label>
        <Select<EntryPaymentMethod>
          id="guest-method"
          value={metodo}
          options={ENTRY_PAYMENT_METHODS.map((m) => ({
            value: m,
            label: t(`paymentMethod.${m}`),
          }))}
          onChange={setMetodo}
        />
      </div>

      <div className="row-actions">
        <button
          type="button"
          disabled={sending || name.trim() === '' || disponibles.length === 0}
          onClick={() => void guardar()}
        >
          {t('tournaments.guestAdd')}
        </button>
        {/* CIERRA LA CAJA, no cancela nada: no hay nada empezado que
            deshacer. Y así deja de chocar con el «Cancelar» del formulario
            de editar el torneo, que puede estar abierto al mismo tiempo. */}
        <button type="button" className="secondary" onClick={() => setAbierta(false)}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}


/**
 * PONER EL TORNEO EN LA VITRINA de torneos abiertos.
 *
 * NO ES LO MISMO QUE EL ENLACE PÚBLICO DEL CUADRO, aunque las dos cosas se
 * llamen «público», y el texto lo dice porque si no se confunden: aquel se
 * comparte a propósito, uno por uno, con los que juegan; esto es aparecer en
 * una lista que recorre cualquiera que abra la aplicación.
 *
 * SE PUEDE PRENDER EN BORRADOR y no aparece hasta que el torneo arranca:
 * quien lo arma lo deja listo el lunes y se olvida. Lo que la vitrina esconde
 * es el borrador, no la decisión — y el texto de ayuda lo dice, porque un
 * interruptor prendido que no hace nada visible se lee como roto.
 */
function PublicToggle({
  tournament,
  onChanged,
}: {
  tournament: Tournament;
  onChanged: (tournament: Tournament) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function cambiar(isPublic: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onChanged(await api.setTournamentPublic(tournament.id, isPublic));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="auto-approve">
        <label className="check">
          <input
            type="checkbox"
            checked={tournament.isPublic}
            disabled={busy}
            onChange={(e) => void cambiar(e.target.checked)}
          />
          <span>{t('tournaments.publicToggle')}</span>
        </label>
        <p className="help">
          {tournament.isPublic
            ? tournament.status === 'DRAFT'
              ? t('tournaments.publicOnDraft')
              : t('tournaments.publicOn')
            : t('tournaments.publicOff')}
        </p>
        {error !== null && <Alert kind="error">{error}</Alert>}
      </div>
    </section>
  );
}

// =====================================================================
// Banner y patrocinadores
// =====================================================================

function BrandingSection({
  tournament,
  onChanged,
}: {
  tournament: Tournament;
  onChanged: (tournament: Tournament) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [banner, setBanner] = useState<File | null>(null);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorLink, setSponsorLink] = useState('');
  const [sponsorImage, setSponsorImage] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Devuelve `true` solo si salió bien: quien llama limpia el formulario únicamente entonces. */
  async function correr(accion: () => Promise<Tournament>): Promise<boolean> {
    setSending(true);
    setError(null);
    try {
      await onChanged(await accion());
      return true;
    } catch (caught) {
      setError(errorMessage(t, caught));
      return false;
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <h2>{t('branding.title')}</h2>
      <p className="help">{t('branding.help')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <ImagePicker
        id="tournament-banner"
        label={t('branding.banner')}
        help={t('branding.bannerHelp')}
        currentUrl={assetUrl(tournament.bannerPath)}
        value={banner}
        disabled={sending}
        onChange={setBanner}
      />
      <div className="button-row">
        <button
          type="button"
          disabled={banner === null || sending}
          onClick={() => {
            if (banner === null) return;
            void correr(() => api.setTournamentBanner(tournament.id, banner)).then((ok) => {
              if (ok) setBanner(null);
            });
          }}
        >
          {t('branding.saveBanner')}
        </button>
        {tournament.bannerPath !== null && (
          <button
            type="button"
            className="link"
            disabled={sending}
            onClick={() => void correr(() => api.removeTournamentBanner(tournament.id))}
          >
            {t('branding.removeBanner')}
          </button>
        )}
      </div>

      <div className="section-head">
        <h3>{t('communities.sponsors')}</h3>
        {/* El CONTADOR siempre, aunque sea cero: es la cifra que quien
            organiza necesita para saber cuánto va a facturar el club, y
            contarlos a ojo en una lista larga es cómo se cobra de menos. */}
        <span className="tag">
          {t('branding.sponsorCount', { count: tournament.sponsors.length })}
        </span>
      </div>

      {/* Y CUANDO NO HAY, SE DICE. Antes la lista simplemente no aparecía, y
          «no hay ninguno» y «esto no cargó» se veían exactamente igual. */}
      {tournament.sponsors.length === 0 ? (
        <p className="help">{t('branding.noSponsors')}</p>
      ) : (
        <ul className="member-list">
          {tournament.sponsors.map((sponsor) => (
            <li className="sponsor-row" key={sponsor.id}>
              <img className="sponsor-thumb" src={assetUrl(sponsor.imagePath) ?? ''} alt={sponsor.name} />
              <div className="member-name">
                <strong>{sponsor.name}</strong>
                {sponsor.linkUrl !== null && (
                  // El enlace se muestra para poder REVISARLO: es lo que se
                  // le prometió al patrocinador, y un enlace roto no se
                  // descubre mirando un logo.
                  <a
                    className="help"
                    href={sponsor.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {sponsor.linkUrl}
                  </a>
                )}
              </div>
              <button
                type="button"
                className="link"
                disabled={sending}
                onClick={() => void correr(() => api.removeTournamentSponsor(tournament.id, sponsor.id))}
              >
                {t('branding.removeSponsor')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="field">
        <label htmlFor="sponsor-name">{t('branding.sponsorName')}</label>
        <input
          id="sponsor-name"
          value={sponsorName}
          disabled={sending}
          maxLength={SPONSOR_LIMITS.nameMax}
          onChange={(e) => setSponsorName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="sponsor-link">{t('branding.sponsorLink')}</label>
        <span className="help" id="sponsor-link-help">
          {t('branding.sponsorLinkHelp')}
        </span>
        <input
          id="sponsor-link"
          type="url"
          value={sponsorLink}
          disabled={sending}
          maxLength={SPONSOR_LIMITS.linkMax}
          aria-describedby="sponsor-link-help"
          onChange={(e) => setSponsorLink(e.target.value)}
        />
      </div>

      <ImagePicker
        id="sponsor-image"
        label={t('branding.sponsorLogo')}
        value={sponsorImage}
        disabled={sending}
        onChange={setSponsorImage}
      />

      <div className="button-row">
        <button
          type="button"
          disabled={sending || sponsorImage === null || sponsorName.trim() === ''}
          onClick={() => {
            if (sponsorImage === null) return;
            void correr(() =>
              api.addTournamentSponsor(tournament.id, sponsorName.trim(), sponsorLink.trim(), sponsorImage),
            ).then((ok) => {
              if (!ok) return;
              setSponsorName('');
              setSponsorLink('');
              setSponsorImage(null);
            });
          }}
        >
          {t('branding.addSponsor')}
        </button>
      </div>
    </section>
  );
}

// =====================================================================
// Tirar el torneo
// =====================================================================

/**
 * Tirar los cuadros. UNO POR UNO.
 *
 * ANTES ERA UN SOLO BOTÓN PARA TODO EL TORNEO y ese era el problema: si al C2
 * femenino le faltaba la cuarta persona, el A masculino con sus dieciséis
 * inscritos se quedaba esperando. Ahora cada categoría tiene su botón y
 * arranca cuando está lista; las demás siguen recibiendo gente.
 *
 * La sección desaparece cuando ya no queda ninguna por tirar: un título con
 * «no hay nada que hacer» debajo es ruido en una pantalla que ya es larga.
 */
function StartSection({
  tournament,
  brackets,
  onChanged,
}: {
  tournament: Tournament;
  brackets: readonly CategoryBracket[];
  onChanged: (tournament: Tournament) => Promise<void>;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const porTirar = tournament.categories.filter((c) => c.startedAt === null);
  if (porTirar.length === 0) return null;

  async function cambiarFormato(
    category: TournamentCategory,
    format: TournamentFormat,
  ): Promise<void> {
    setError(null);
    try {
      await onChanged(
        await api.setCategoryFormat(tournament.id, {
          level: category.level,
          sex: category.sex,
          discipline: category.discipline,
          format,
        }),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function tirar(category: TournamentCategory): Promise<void> {
    setSending(true);
    setError(null);
    try {
      await onChanged(
        await api.startTournament(tournament.id, {
          level: category.level,
          sex: category.sex,
          discipline: category.discipline,
        }),
      );
      setConfirming(null);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <h2>{t('tournaments.startTitle')}</h2>
      <p className="help">{t('tournaments.startHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <ul className="member-list">
        {porTirar.map((category) => {
          const clave = categoryKey(category);
          const cuadro = brackets.find(
            (b) => categoryKey(b) === categoryKey(category),
          );
          const inscritos = tournament.entries.filter(
            (e) => categoryKey(e) === categoryKey(category),
          ).length;
          // Es la misma regla que aplica el servidor; acá sirve para explicar
          // POR QUÉ el botón no va a funcionar, en vez de dejar que reviente.
          // Cada formato tiene la suya: las llaves piden un cuadro posible y
          // las tablas, gente suficiente para llenar sus grupos.
          const puede =
            category.format === 'KNOCKOUT'
              ? cuadro !== undefined && cuadro.problem === null
              : inscritos >= minimumEntriesFor(category.format);

          /*
           * «C · Femenino», para el encabezado Y para los nombres accesibles.
           *
           * Se calcula una vez y se usa en los tres lugares: si cada uno
           * armara el suyo, el día que cambie la forma de escribir una
           * categoría el lector de pantalla diría una cosa y la pantalla otra.
           */
          const nombreCategoria = categoryName(t, category);

          return (
            <li key={clave}>
              <div className="member-name">
                <strong>{nombreCategoria}</strong>
                {/*
                  EL FORMATO SE ELIGE ACÁ, al lado del botón de tirar.

                  Es la última decisión antes de que el cuadro quede fijo, y
                  después de tirarlo ya no se puede cambiar: los partidos se
                  crean con la forma del formato. Ponerlo en otra pantalla
                  haría que quien va a tirar el torneo no lo vea.
                */}
                <span className="field format-pick">
                  <Select<TournamentFormat>
                    id={`format-${clave}`}
                    // DE CUÁL CUADRO. Hay uno por categoría y todos decían
                    // «Formato»: con dos categorías corriendo, un lector de
                    // pantalla anuncia dos desplegables idénticos.
                    aria-label={t('format.labelOf', { category: nombreCategoria })}
                    value={category.format}
                    disabled={sending}
                    options={TOURNAMENT_FORMATS.map((format) => ({
                      value: format,
                      label: t(`format.${format}`),
                    }))}
                    onChange={(format) => void cambiarFormato(category, format)}
                  />
                </span>
                {!puede && (
                  <span className="help">
                    {category.format === 'KNOCKOUT'
                      ? t('tournaments.cannotStart')
                      : t('format.needMore', { n: minimumEntriesFor(category.format) })}
                  </span>
                )}
              </div>

              {confirming === clave ? (
                <span className="join-actions">
                  {/* Se confirma en la misma fila y no con un `confirm()` del
                      navegador: ese cuadro no se puede leer con calma, no se
                      traduce y en el celular tapa todo. */}
                  <button type="button" disabled={sending} onClick={() => void tirar(category)}>
                    {sending ? t('common.sending') : t('tournaments.startConfirmYes')}
                  </button>
                  <button
                    type="button"
                    className="link"
                    disabled={sending}
                    onClick={() => setConfirming(null)}
                  >
                    {t('common.cancel')}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  disabled={!puede}
                  /*
                    CUÁL CUADRO SE VA A SORTEAR. Tirar NO SE DESHACE: los
                    partidos se crean con ese orden y después hay resultados
                    contra él. Con dos categorías corriendo, dos botones que
                    se anuncian igual son dos formas de sortear la equivocada.
                  */
                  aria-label={t('tournaments.startOf', { category: nombreCategoria })}
                  onClick={() => setConfirming(clave)}
                >
                  {t('tournaments.start')}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {confirming !== null && <Alert kind="info">{t('tournaments.startConfirm')}</Alert>}
    </section>
  );
}

/**
 * Quiénes están inscritos, sin el cuadro.
 *
 * Es lo que ve cualquiera mientras el torneo está en borrador. No lleva
 * números de sembrado a propósito: en borrador el orden todavía puede
 * cambiar, y un número al lado del nombre se lee como una posición ganada.
 */
function EntryList({
  entries,
  de,
}: {
  entries: readonly TournamentEntry[];
  /** Qué categoría es esta, para que su paginador tenga nombre propio. */
  de: string;
}): JSX.Element {
  const { t } = useTranslation();

  /**
   * ORDENADO ANTES DE PAGINAR, y por eso el `useMemo`: sin él la lista se
   * reordena en cada dibujado y la página 2 puede traer a alguien que ya
   * estaba en la 1. Ordenar dentro del `.map()` era inofensivo mientras se
   * dibujaban todos; con páginas deja de serlo.
   */
  const ordenados = useMemo(
    () => [...entries].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es')),
    [entries],
  );
  const pagina = usePaginado(ordenados);

  if (entries.length === 0) {
    return <p className="help">{t('tournaments.noEntriesYet')}</p>;
  }

  return (
    <>
      <p className="help">{t('tournaments.entriesOnlyHelp')}</p>
      <div className="table-scroll">
        <table className="people-table">
          <thead>
            <tr>
              <th scope="col">{t('player.player')}</th>
              <th scope="col">{t('ranking.category')}</th>
            </tr>
          </thead>
          <tbody>
            {pagina.visibles.map((entry) => (
                <tr key={entry.id}>
                  <PersonCell
                    userId={entry.userId}
                    displayName={entry.displayName}
                    avatarPath={entry.avatarPath}
                  />
                  <td>
                    {t(`categoryLevel.${entry.level}`)}
                    {entry.isSecondCategory && (
                      <>
                        {' '}
                        <span className="tag">{t('tournaments.second')}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <Paginador
        pagina={pagina.pagina}
        paginas={pagina.paginas}
        irA={pagina.irA}
        de={de}
      />
    </>
  );
}

/**
 * EL REGLAMENTO DE ESTE TORNEO, con el del club abajo de todo.
 *
 * Javier: «en los torneos debo poner un reglamento que se copia y pega
 * automáticamente para un nuevo torneo que vaya a crear».
 *
 * EL BORRADOR ARRANCA CON LO QUE SE ESTÁ LEYENDO, que casi siempre es el del
 * club. Eso ES el «se copia y pega»: quien organiza abre el editor y ya tiene
 * el texto de la casa adentro, le cambia el renglón que quiera y guarda. No
 * hay un botón de «copiar del club» porque no hace falta apretarlo.
 *
 * VACIARLO NO LO DEJA SIN REGLAMENTO: lo devuelve al del club. Por eso el
 * botón dice «volver al del club» y solo aparece cuando este torneo escribió
 * el suyo —si no, no hay a dónde volver—.
 *
 * SIN REGLAMENTO Y SIN PODER ESCRIBIRLO, la sección no se dibuja: sería un
 * título con un «todavía no hay» debajo, que es ruido para quien solo mira.
 */
function TournamentRules({
  tournament,
  onChanged,
}: {
  tournament: Tournament;
  onChanged: (tournament: Tournament) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tournament.rules ?? '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rules = tournament.rules;
  const vacío = rules === null || rules.trim() === '';

  if (vacío && !tournament.canManage) return null;

  async function guardar(texto: string): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(await api.setTournamentRules(tournament.id, texto));
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>{t('rules.title')}</h2>
        {tournament.canManage && !editing && (
          <button
            type="button"
            className="link"
            // EL NOMBRE DICE QUÉ EDITA. Estas pantallas tienen dos y tres
            // botones «Editar» —la ficha, el logo, el reglamento— y todos se
            // llamaban igual: con el título al lado se entiende mirando, pero
            // quien usa un lector de pantalla oye «Editar, Editar, Editar».
            // Solo cuando dice «Editar»: con «Escribir el reglamento» el texto
            // visible ya es específico, y un nombre accesible distinto del que
            // se lee en pantalla confunde a quien usa las dos cosas.
            aria-label={vacío ? undefined : t('rules.edit')}
            onClick={() => {
              // El borrador arranca con lo que se está leyendo: si este
              // torneo no escribió el suyo, eso es el del club, y ahí está la
              // copia que pidió Javier.
              setDraft(tournament.rules ?? '');
              setError(null);
              setEditing(true);
            }}
          >
            {vacío ? t('rules.write') : t('common.edit')}
          </button>
        )}
      </div>

      <p className="help">
        {tournament.rulesAreOwn ? t('tournaments.rulesOwn') : t('tournaments.rulesFromClub')}
      </p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {editing ? (
        <>
          <div className="field">
            {/* NO `rules.label`, que dice «Reglamento del club»: acá se está
                editando el de ESTE torneo, y una etiqueta que diga otra cosa
                es la forma más barata de que alguien crea que está cambiando
                el de la casa. */}
            <label htmlFor="tournament-rules">{t('tournaments.rulesLabel')}</label>
            <textarea
              id="tournament-rules"
              rows={12}
              value={draft}
              disabled={sending}
              maxLength={TOURNAMENT_LIMITS.rulesMax}
              placeholder={t('rules.placeholder')}
              onChange={(e) => setDraft(e.target.value)}
            />
            {/* El contador solo cuando falta poco: con doscientos mil de tope,
                uno permanente es ruido que nadie va a leer nunca. */}
            {TOURNAMENT_LIMITS.rulesMax - draft.length <= 500 && (
              <p className="help">
                {t('rules.remaining', { n: TOURNAMENT_LIMITS.rulesMax - draft.length })}
              </p>
            )}
          </div>
          <div className="button-row">
            <button type="button" onClick={() => void guardar(draft.trim())} disabled={sending}>
              {sending ? t('common.sending') : t('common.save')}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => setEditing(false)}
              disabled={sending}
            >
              {t('common.cancel')}
            </button>
            {/* SOLO CUANDO HAY A DÓNDE VOLVER. Con el reglamento del club a la
                vista, este botón no haría nada y estaría igual de visible. */}
            {tournament.rulesAreOwn && (
              <button
                type="button"
                className="link danger"
                onClick={() => void guardar('')}
                disabled={sending}
              >
                {t('tournaments.rulesBackToClub')}
              </button>
            )}
          </div>
        </>
      ) : vacío ? (
        <p className="help">{t('rules.empty')}</p>
      ) : (
        // `white-space: pre-wrap` en el CSS: el reglamento viene con saltos de
        // línea y numeración a mano, y respetarlos es la diferencia entre un
        // reglamento y un párrafo ilegible.
        <p className="rules-text">{rules}</p>
      )}
    </section>
  );
}
